import { useMemo } from 'react';

import { Modal } from 'antd';
import dayjs from 'dayjs';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getAnalysisKindMeta } from '@/features/map/utils/analysisKindMeta';
import { enhancedFloodPointFromFloodJob } from '@/features/map/utils/floodJobToEnhancedFloodPoint';
import type { FloodJob } from '@/mocks/floodJobs';

import { EnhancedFloodPointInsightPanel } from './EnhancedFloodPointInsightPanel';

export interface SarJobResultModalProps {
  open: boolean;
  job: FloodJob | null;
  onClose: () => void;
}

/**
 * SAR job results: AOI insight (tabs như popup bản đồ; ảnh viễn thám trong tab Viễn thám).
 */
export function SarJobResultModal({ open, job, onClose }: SarJobResultModalProps) {
  const { t } = useTranslation();

  const insightPoint = useMemo(() => (job ? enhancedFloodPointFromFloodJob(job) : null), [job]);

  const landCoverDateRange = useMemo(() => {
    if (!job?.parameters) return undefined;
    const p = job.parameters;
    const min = dayjs(p.baseline);
    const max = dayjs(p.crisisEnd ?? p.crisis);
    if (!min.isValid() || !max.isValid()) return undefined;
    return {
      minIso: min.startOf('day').toISOString(),
      maxIso: max.endOf('day').toISOString(),
    };
  }, [job]);

  if (!job || !insightPoint) return null;

  const isLandCoverJob =
    job.parameters.analysisKind === 'landCover' ||
    String((job as any).task_type)
      .toLowerCase()
      .includes('land');
  const kindMeta = getAnalysisKindMeta(isLandCoverJob ? 'landCover' : job.parameters.analysisKind);
  const KindIcon = kindMeta.Icon;

  return (
    <Modal
      title={
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}
        >
          <KindIcon size={18} color={kindMeta.accentColor} />
          <span>{t(kindMeta.longLabelKey)}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1320px, 98vw)"
      style={{ top: 24, paddingBottom: 0 }}
      destroyOnHidden
      styles={{
        body: { background: 'var(--mdm-surface)', padding: 16, color: 'var(--text-primary)' },
        header: {
          background: 'transparent',
          borderBottom: '1px solid var(--glass-border)',
        },
      }}
      className="dark-modal"
    >
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{job.name}</div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          {job.aoi && job.aoi !== '—' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={12} />
              {job.aoi}
            </span>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-[var(--glass-border)] bg-[var(--map-chrome-panel)]">
        <EnhancedFloodPointInsightPanel
          floodPoint={insightPoint}
          contentMaxHeight="min(72vh, 800px)"
          spaciousRemoteGallery
          hideHistoryTab
          landCoverOnly={isLandCoverJob}
          landCoverDateRange={landCoverDateRange}
        />
      </div>
    </Modal>
  );
}
